import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { SealWarning } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '@/helpers/hooks/useSnackbar'
import { getApiErrorMessage } from '@/utils/error'

import API from '../../../../api/api'
import { maintenanceRequestStatusTypes, parseToSelect } from '../../MaintenanceRequests/helpers'
import { updateSelectedMaintenanceRequest } from '../../../../reducers/maintenanceReducer'

import NotesModal from './NotesModal'
import AddNoteModal from './AddNoteModal'
import DescriptionModal from './DescriptionModal'
import JobSummaryModal from './JobSummaryModal'
import CompleteRequestModal from './CompleteRequestModal'
import CancelMaintenanceRequestModal from './CancelMaintenanceRequestModal'
import MaintenanceRequestDetailsBody from './MaintenanceRequestDetailsBody'
import MaintenanceRequestPreviewNavbar from './MaintenanceRequestPreviewNavbar'

const MaintenanceRequestDetails = ({
  data,
  getRecentActivityData,
  buildingData,
  updateSelectedMaintenanceRequest,
}) => {
  const { t } = useTranslation(['maintenance'])
  const { displaySnackbar } = useSnackbar()
  const [isLoading, setLoading] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false)
  const [staffList, setStaffList] = useState([])
  const [description, setDescription] = useState(data.description)
  const [entryNote, setEntryNota] = useState(data.entryNote)
  const [jobSummaryMessage, setJobSummaryMessage] = useState(data.jobSummaryMessage)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [showAddNoteModal, setShowAddNoteModal] = useState(false)
  const [showDescriptionModal, setShowDescriptionModal] = useState(false)
  const [showEntryNoteModal, setShowEntryNoteModal] = useState(false)
  const [showJobSummaryModal, setShowJobSummaryModal] = useState(false)
  const [showCompleteRequestModal, setShowCompleteRequestModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [realPageConfig, setRealPageConfig] = useState(null)
  const [entrataConfig, setEntrataConfig] = useState(null)
  const [resmanConfig, setResmanConfig] = useState(null)
  const [yardiConfig, setYardiConfig] = useState(null)
  const [rentManagerConfig, setRentManagerConfig] = useState(null)
  const [notes, setNotes] = useState([])
  const timeout = useRef(null)

  const [status, setStatus] = useState(
    parseToSelect(maintenanceRequestStatusTypes).find(
      (statusData) => statusData.text === data.status,
    ),
  )

  const getNotes = () => {
    API.getNotes(data.id)
      .then(({ data }) => {
        setNotes(data)
      })
      .catch(() => {
        setErrorMessage(t('maintenance:requestPreview.details.errors.couldNotLoadNotes'))
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
      })
  }

  const getRealPageConfig = () => {
    setLoading(true)
    API.getRealPageConfig(buildingData.id)
      .then(({ data }) => {
        setRealPageConfig(data)
      })
      .catch(() => {
        setErrorMessage(t('maintenance:requestPreview.details.errors.couldNotLoadRealPageConfig'))
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
      })
      .finally(() => {
        setLoading(false)
      })
  }
  const getEntrataConfig = () => {
    setLoading(true)
    API.getEntrataConfig(
      buildingData.id,
      data.clientId ? data.clientId.entrataPropertyId : data.propertyId || null,
    )
      .then(({ data }) => {
        setEntrataConfig(data)
      })
      .catch(() => {
        setErrorMessage(t('maintenance:requestPreview.details.errors.couldNotLoadEntrataConfig'))
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
      })
      .finally(() => {
        setLoading(false)
      })
  }
  const getResmanConfig = () => {
    setLoading(true)
    API.getResmanConfig(buildingData.id)
      .then(({ data }) => {
        setResmanConfig(data)
      })
      .catch(() => {
        setErrorMessage(t('maintenance:requestPreview.details.errors.couldNotLoadResmanConfig'))
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
      })
      .finally(() => {
        setLoading(false)
      })
  }
  const getYardiConfig = () => {
    setLoading(true)
    API.getYardiConfig(buildingData.id)
      .then(({ data }) => {
        setYardiConfig(data)
      })
      .catch(() => {
        setErrorMessage(t('maintenance:requestPreview.details.errors.couldNotLoadYardiConfig'))
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const getRentManagerConfig = async () => {
    try {
      const { data } = await API.getRentManagerConfig(buildingData.id)
      setRentManagerConfig(data)
    } catch {
      setErrorMessage(t('maintenance:requestPreview.details.errors.couldNotLoadRentManagerConfig'))
      timeout.current = setTimeout(() => {
        setErrorMessage(null)
      }, 2000)
    }
  }

  const getStaffList = () => {
    API.getAssigneesForFilter(buildingData.id)
      .then(({ data }) => {
        setStaffList(
          data.map((staff) => ({
            text: `${staff.user.firstName} ${staff.user.lastName}`,
            id: staff.id,
            value: staff.id,
            ...staff,
          })),
        )
      })
      .catch(() => {
        setErrorMessage(t('maintenance:requestPreview.details.errors.couldNotLoadAssignees'))
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
      })
  }

  const getNotesAndActivities = () => {
    getNotes()
    getRecentActivityData()
  }

  useEffect(() => {
    if (buildingData.realPagePropertyId) {
      getRealPageConfig()
    } else if (buildingData.entrataPropertyId) {
      getEntrataConfig()
    } else if (buildingData.resmanPropertyId) {
      getResmanConfig()
    } else if (buildingData.yardiPropertyId) {
      getYardiConfig()
    } else if (buildingData.rentManagerPropertyId) {
      getRentManagerConfig()
    }

    getStaffList()
    getNotes()
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  const updateMaintenanceRequestData = async (dataToUpdate, dataToRewriteLocal, onError) => {
    try {
      setLoading(true)
      await updateSelectedMaintenanceRequest(
        data.id,
        dataToUpdate,
        dataToRewriteLocal || dataToUpdate,
      )
      return true
    } catch (err) {
      console.error(err)
      onError?.(err)
      return false
    } finally {
      setLoading(false)
    }
  }

  const updateMaintenanceRequestValue = async (dataToUpdate, dataToRewriteLocal, onError) => {
    const isUpdated = await updateMaintenanceRequestData(
      dataToUpdate,
      dataToRewriteLocal || dataToUpdate,
      onError,
    )
    getRecentActivityData()
    return isUpdated
  }

  const cancelMaintenanceRequest = async (cancellationReason) => {
    setShowCancelRequestModal(false)

    await updateMaintenanceRequestValue(
      {
        cancellationReason,
        status: 'Canceled',
      },
      undefined,
      (err) => displaySnackbar(getApiErrorMessage(err?.response?.data), true),
    )

    setStatus(
      parseToSelect(maintenanceRequestStatusTypes).find(
        (statusData) => statusData.text === 'Canceled',
      ),
    )
  }

  const updateCompleteMaintenanceRequestData = async (data) => {
    const isUpdated = await updateMaintenanceRequestValue(data, undefined, (err) =>
      displaySnackbar(getApiErrorMessage(err?.response?.data), true),
    )

    // The BE saves the job summary even when completion is rejected (e.g. required assignee /
    // scheduled date missing), so keep it, but only move the status when the update succeeded —
    // otherwise Complete Request / Edit get disabled for a WO that is still open.
    setJobSummaryMessage(data.jobSummaryMessage)
    if (!isUpdated) return

    setStatus(
      parseToSelect(maintenanceRequestStatusTypes).find(
        (statusData) => statusData.text === data.status,
      ),
    )
  }

  const handleEditMode = (isEditMode) => setEditMode(isEditMode)

  return (
    <React.Fragment>
      <MaintenanceRequestPreviewNavbar
        data={data}
        timezone={buildingData.timezone}
        setShowCompleteRequestModal={setShowCompleteRequestModal}
        setShowCancelRequestModal={setShowCancelRequestModal}
        handleEditMode={handleEditMode}
        isEditMode={editMode}
        status={status}
      />
      {!!data.emergency && (
        <div className="emergency-request-container">
          <div className="emergency-request-container-icon-wrap">
            <SealWarning size={20} />
          </div>
          <p className="emergency-request-container-text">
            {t('maintenance:requestPreview.details.emergencyBanner')}
          </p>
        </div>
      )}
      <MaintenanceRequestDetailsBody
        data={data}
        notes={notes}
        isEditMode={editMode}
        errorMessage={errorMessage}
        jobSummaryMessage={jobSummaryMessage}
        status={status}
        setStatus={setStatus}
        isLoading={isLoading}
        setShowNotesModal={setShowNotesModal}
        setShowJobSummaryModal={setShowJobSummaryModal}
        setShowCompleteRequestModal={setShowCompleteRequestModal}
        setDescription={setDescription}
        setShowAddNoteModal={setShowAddNoteModal}
        description={description}
        entryNote={entryNote}
        setShowEntryNoteModal={setShowEntryNoteModal}
        setShowDescriptionModal={setShowDescriptionModal}
        staffList={staffList}
        buildingData={buildingData}
        realPageConfig={realPageConfig}
        entrataConfig={entrataConfig}
        resmanConfig={resmanConfig}
        yardiConfig={yardiConfig}
        rentManagerConfig={rentManagerConfig}
        updateMaintenanceRequestValue={updateMaintenanceRequestValue}
      />
      <CancelMaintenanceRequestModal
        cancelMaintenanceRequest={cancelMaintenanceRequest}
        showModal={showCancelRequestModal}
        setShowModal={setShowCancelRequestModal}
        yardiConfig={yardiConfig}
      />
      {showNotesModal && (
        <NotesModal
          notes={notes}
          setShowAddNoteModal={setShowAddNoteModal}
          handleCancel={() => setShowNotesModal(false)}
        />
      )}
      {showAddNoteModal && (
        <AddNoteModal
          getNotesAndActivities={getNotesAndActivities}
          maintenanceId={data.id}
          handleCancel={() => setShowAddNoteModal(false)}
        />
      )}
      {showDescriptionModal && (
        <DescriptionModal
          description={description}
          setDescription={setDescription}
          updateDescriptionData={updateMaintenanceRequestData}
          handleCancel={() => setShowDescriptionModal(false)}
          name="description"
          placeholder={t('maintenance:requestPreview.details.description.placeholder')}
          title={t('maintenance:requestPreview.details.description.title')}
        />
      )}
      {showEntryNoteModal && (
        <DescriptionModal
          description={entryNote}
          setDescription={setEntryNota}
          updateDescriptionData={updateMaintenanceRequestData}
          handleCancel={() => setShowEntryNoteModal(false)}
          name="entryNote"
          placeholder={t('maintenance:requestPreview.details.entryNote.placeholder')}
          title={t('maintenance:requestPreview.details.entryNote.title')}
        />
      )}
      {showJobSummaryModal && (
        <JobSummaryModal
          jobSummaryMessage={jobSummaryMessage}
          setJobSummaryMessage={setJobSummaryMessage}
          jobSummaryPhotos={data.jobSummaryPhotos}
          updateJobSummaryData={updateMaintenanceRequestData}
          handleCancel={() => setShowJobSummaryModal(false)}
        />
      )}
      {showCompleteRequestModal && (
        <CompleteRequestModal
          maintenanceId={data.id}
          jobSummaryMessage={jobSummaryMessage}
          setJobSummaryMessage={setJobSummaryMessage}
          handleCancel={() => setShowCompleteRequestModal(false)}
          updateCompleteMaintenanceRequestData={updateCompleteMaintenanceRequestData}
        />
      )}
    </React.Fragment>
  )
}

MaintenanceRequestDetails.propTypes = {
  data: PropTypes.object,
  buildingData: PropTypes.object,
  updateSelectedMaintenanceRequest: PropTypes.func,
  getRecentActivityData: PropTypes.func,
}

export default connect(() => ({}), {
  updateSelectedMaintenanceRequest,
})(MaintenanceRequestDetails)
